import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    Color,
    LifecycleAware,
    Modal,
    Orientation,
    forLifeOf,
    DatePickerType,
    ClassNames
} from '@lsq-frontend/common-ui';
import { ClaimService, Icons, WorkflowMonitorService, LsqDatePipe } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ManageSupplierService } from '../../manage-supplier.service';
import { EnrollmentStatus } from '@lsq-frontend/remote-services/all-java/financing-mk1-services';
import {
    GetSupplierAgreementStatusRequest,
    GetSupplierAgreementStatusResponse,
    GetSupplierAgreementStatusService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-supplier-agreement/get-supplier-agreement-status';
import { AdminSuppliersService } from '../../../admin-suppliers.service';
import { GetSupplierRelationshipDetailsResponse } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-supplier/get-supplier-relationship-details';
import { ToggleAlternativeAgreementModalComponent } from '../../../modals/toggle-using-alternative-agreement-modal.component';
import {
    SetIsUsingAlternateSupplierAgreementRequest,
    SetIsUsingAlternateSupplierAgreementService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-supplier-agreement/set-is-using-alternate-supplier-agreement';
import { AgreementCompleteCheckModalComponent } from '../../../modals/agreement-complete-check-modal.component';
import {
    SaveAgreementCompletedRequest,
    SaveAgreementCompletedService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-supplier-agreement/save-agreement-completed';
import {
    UnsaveAgreementCompletedRequest,
    UnsaveAgreementCompletedService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-supplier-agreement/unsave-agreement-completed';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
@LifecycleAware()
@Component({
    selector: 'supplier-implementation-agreement',
    styleUrls: ['./supplier-implementation-agreement.component.scss'],
    template: `
        <card [headerTitle]="supplierAgreementHeaderI18n" [style.marginBottom]="'3rem'">
            <div card-body *ngIf="!agreementFormGroup" class="implementation-card-body">
                <lsq-circle-loader></lsq-circle-loader>
            </div>
            <div card-body *ngIf="!!agreementFormGroup" class="implementation-card-body">
                <div class="agreement-structure-controls">
                    <form class="control-inputs" [formGroup]="agreementFormGroup">
                        <div class="control-row" [style.marginBottom]="'1.8rem'">
                            <lsq-switch
                                [labelOrientation]="orientation.Right"
                                formControlName="usingAlternateSupplierAgreement"
                                name="usingAlternateSupplierAgreement"
                                [group]="agreementFormGroup"
                                [style.marginRight]="'1.5rem'"
                                id="usingAlternateSupplierAgreement"
                            >
                            </lsq-switch>
                            <div [style.width]="'100%'">
                                <lsq-label
                                    [label]="usingAlternateAgreementLabelI18n"
                                    [subLabel]="usingAlternateAgreementSubLabelI18n"
                                ></lsq-label>
                            </div>
                        </div>
                        <div class="control-row" [style.paddingLeft]="'5rem'">
                            <lsq-checkbox
                                formControlName="agreementCompleteCheck"
                                name="agreementCompleteCheck"
                                id="agreementCompleteCheck"
                                [group]="agreementFormGroup"
                                [style.marginRight]="'0.5rem'"
                            ></lsq-checkbox>
                            <div [style.width]="'100%'">
                                <lsq-label
                                    label="Supplier Agreement Complete"
                                    subLabel="Enter the date the alternative electronic or physical supplier agreement was signed."
                                    [disabled]="disableAgreementCompleteCheck"
                                ></lsq-label>
                                <div class="control-inputs">
                                    <single-date-input
                                        [style.marginRight]="'0.8rem'"
                                        name="supplierAgreementSignedDate"
                                        placeholder="Select a date"
                                        [allowFuture]="false"
                                        formControlName="supplierAgreementSignedDate"
                                        [group]="agreementFormGroup"
                                    ></single-date-input>
                                    <lsq-button
                                        id="saveSupplierAgreementDateButton"
                                        (buttonClick)="openAgreementCompletedModal()"
                                        [disabled]="!(agreementCompleteHasChanges$ | async)"
                                    >
                                        Save
                                    </lsq-button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </card>
    `
})
export class ManageSupplierImplementationAgreementComponent implements OnInit, OnDestroy {
    public readonly icons = Icons;
    public readonly color = Color;
    public readonly orientation = Orientation;
    public readonly enrollmentStatus = EnrollmentStatus;
    public readonly datePickerType = DatePickerType;
    public readonly today = new Date();

    public agreementCompleteHasChanges$: Observable<boolean>;
    public relationship: GetSupplierRelationshipDetailsResponse;
    public agreementFormGroup: FormGroup;
    public disableAgreementCompleteCheck = false;
    public canEditSuppliers = false;

    private _openAlternativeAgreementModalSubscription: Subscription;
    private _agreementCompleteCheckedSubscription: Subscription;
    private _agreementCompleteHasChangesSubscription: Subscription;

    constructor(
        private _manageSupplierService: ManageSupplierService,
        private _adminSuppliersService: AdminSuppliersService,
        private _modalService: ModalService,
        private _claimService: ClaimService,
        private _i18n: I18n,
        private _getSupplierAgreementStatusService: GetSupplierAgreementStatusService,
        private _setIsUsingAlternateSupplierAgreementService: SetIsUsingAlternateSupplierAgreementService,
        private _saveAgreementCompletedService: SaveAgreementCompletedService,
        private _unsaveAgreementCompletedService: UnsaveAgreementCompletedService,
        private _workflowMonitorService: WorkflowMonitorService,
        private lsqDatePipe: LsqDatePipe
    ) {}

    public ngOnInit(): void {
        this.canEditSuppliers = this._claimService.isOnSync(DashboardClaim.AdminEditSupplier);

        this._manageSupplierService.currentSupplierRelationship$
            .pipe(
                forLifeOf(this),
                filter((relationship) => !!relationship)
            )
            .subscribe(async (relationship) => {
                this.relationship = relationship;

                const supplierAgreementStatusResponse = await this._getSupplierAgreementStatusService.executeOnce(
                    new GetSupplierAgreementStatusRequest({
                        relationshipKey: this.relationship?.relationshipKey
                    })
                );

                this._setupForm(supplierAgreementStatusResponse);
                this._setupSubscriptions(supplierAgreementStatusResponse);

                this.disableAgreementCompleteCheck = !supplierAgreementStatusResponse.usingAlternateSupplierAgreement;
            });
    }

    private _setupForm(supplierAgreementStatusResponse: GetSupplierAgreementStatusResponse): void {
        const supplierIsEnrolled = this._determineIfEnrolled(this.relationship);

        this.agreementFormGroup = new FormGroup({
            usingAlternateSupplierAgreement: new FormControl(
                {
                    value: supplierAgreementStatusResponse.usingAlternateSupplierAgreement,
                    disabled:
                        !!supplierAgreementStatusResponse.supplierAgreementSignedDate ||
                        !this.canEditSuppliers ||
                        supplierIsEnrolled
                },
                [Validators.required]
            ),
            agreementCompleteCheck: new FormControl(
                {
                    value: !!supplierAgreementStatusResponse.supplierAgreementSignedDate,
                    disabled:
                        !supplierAgreementStatusResponse.usingAlternateSupplierAgreement ||
                        !this.canEditSuppliers
                },
                [Validators.required]
            ),
            supplierAgreementSignedDate: new FormControl(
                {
                    value: !!supplierAgreementStatusResponse.supplierAgreementSignedDate
                        ? new Date(supplierAgreementStatusResponse.supplierAgreementSignedDate)
                        : null,
                    disabled:
                        !supplierAgreementStatusResponse.usingAlternateSupplierAgreement ||
                        !supplierAgreementStatusResponse.supplierAgreementSignedDate ||
                        !this.canEditSuppliers
                },
                [Validators.required, LsqValidators.noFutureDate]
            )
        });
    }

    private _setupSubscriptions(
        supplierAgreementStatusResponse: GetSupplierAgreementStatusResponse
    ): void {
        if (this._openAlternativeAgreementModalSubscription) {
            this._openAlternativeAgreementModalSubscription.unsubscribe();
        }

        if (this._agreementCompleteCheckedSubscription) {
            this._agreementCompleteCheckedSubscription.unsubscribe();
        }
        if (this._agreementCompleteHasChangesSubscription) {
            this._agreementCompleteHasChangesSubscription.unsubscribe();
        }

        this.agreementCompleteHasChanges$ = this.agreementFormGroup.valueChanges.pipe(
            map(
                (formValues: {
                    usingAlternateSupplierAgreement: boolean;
                    agreementCompleteCheck: boolean;
                    supplierAgreementSignedDate: Date;
                }) =>
                    (formValues.agreementCompleteCheck &&
                        !supplierAgreementStatusResponse.supplierAgreementSignedDate) ||
                    (formValues.supplierAgreementSignedDate !== undefined &&
                        formValues.supplierAgreementSignedDate !==
                            supplierAgreementStatusResponse.supplierAgreementSignedDate)
            )
        );

        this._agreementCompleteHasChangesSubscription = this.agreementCompleteHasChanges$.subscribe(
            (hasChanges) => {
                if (hasChanges) {
                    this._workflowMonitorService.markWorkflowStarted();
                } else {
                    this._workflowMonitorService.markWorkflowFinished();
                }
            }
        );

        this._agreementCompleteCheckedSubscription = this.agreementFormGroup
            .get('agreementCompleteCheck')
            .valueChanges.subscribe((isChecked) => {
                if (isChecked) {
                    this.agreementFormGroup.get('supplierAgreementSignedDate').enable();
                } else {
                    this.agreementFormGroup.get('supplierAgreementSignedDate').disable();
                }

                // Open the disabling modal when unchecking if has existing signed date.
                if (!isChecked && !!supplierAgreementStatusResponse.supplierAgreementSignedDate) {
                    this.openAgreementCompletedModal(true);
                }
            });

        // Open the Alternative Agreement confirmation modal when the toggle is switched.
        this._openAlternativeAgreementModalSubscription = this.agreementFormGroup
            .get('usingAlternateSupplierAgreement')
            .valueChanges.subscribe((isToggledOn) => {
                this.showAlternativeAgreementModal(isToggledOn);
            });
    }

    public ngOnDestroy(): void {
        this._workflowMonitorService.markWorkflowFinished();
        this._openAlternativeAgreementModalSubscription?.unsubscribe();
        this._agreementCompleteCheckedSubscription?.unsubscribe();
    }

    public openAgreementCompletedModal(disablingAction?: boolean) {
        this.agreementFormGroup.updateValueAndValidity();
        this.agreementFormGroup.markAllAsTouched();

        if (this.agreementFormGroup.invalid && !disablingAction) {
            return;
        }

        const agreementCompletedStatus = this.agreementFormGroup.controls.agreementCompleteCheck
            .value;
        const isSubmitting$ = new BehaviorSubject<boolean>(false);
        this._modalService.showModal(
            new Modal({
                id: 'confirm-changes-supplier-alternative-agreement',
                title: this.modalAgreementCompletedTitle,
                showFooter: true,
                showOkButton: true,
                bodyComponent: AgreementCompleteCheckModalComponent,
                data: {
                    agreementCompleted: agreementCompletedStatus,
                    supplierRelationship: this.relationship
                },
                okButtonText: this.modalAgreementAlternativeConfirm18n,
                okButtonSize: ClassNames.Block,
                showCancelButton: true,
                cancelButtonText: this.modalAgreementAlternativeCancelI18n,
                isSubmitting$,
                onOkClick: async () => {
                    if (disablingAction) {
                        this.unsaveAgreementCompleted(isSubmitting$);
                    } else {
                        this.saveAgreementCompleted(isSubmitting$);
                    }
                },
                onBackdropClick: async () => {
                    if (disablingAction) {
                        this.closeAgreementCompletedModal(agreementCompletedStatus);
                    } else {
                        this._modalService.closeModal();
                    }
                },
                onCancelClick: async () => {
                    if (disablingAction) {
                        this.closeAgreementCompletedModal(agreementCompletedStatus);
                    } else {
                        this._modalService.closeModal();
                    }
                }
            })
        );
    }

    private showAlternativeAgreementModal(isToggledOn: boolean) {
        const isSubmitting$ = new BehaviorSubject<boolean>(false);
        this._modalService.showModal(
            new Modal({
                id: 'confirm-changes-supplier-alternative-agreement',
                title: isToggledOn
                    ? this.modalAgreementAlternativeEnableHeaderI18n
                    : this.modalAgreementAlternativeDisableHeaderI18n,
                showFooter: true,
                showOkButton: true,
                bodyComponent: ToggleAlternativeAgreementModalComponent,
                data: {
                    usingAlternateAgreement: this.agreementFormGroup.controls
                        .usingAlternateSupplierAgreement.value,
                    supplierRelationship: this.relationship
                },
                okButtonText: this.modalAgreementAlternativeConfirm18n,
                showCancelButton: true,
                okButtonSize: ClassNames.Block,
                cancelButtonText: this.modalAgreementAlternativeCancelI18n,
                isSubmitting$,
                onOkClick: async () => {
                    this.setIsUsingAlternateSupplierAgreement(isSubmitting$);
                },
                onBackdropClick: async () => {
                    this.closeUsingAlternateAgreementModal(isToggledOn);
                },
                onCancelClick: async () => {
                    this.closeUsingAlternateAgreementModal(isToggledOn);
                }
            })
        );
    }

    private closeUsingAlternateAgreementModal(isToggledOn: boolean): void {
        this.agreementFormGroup.patchValue({
            usingAlternateSupplierAgreement: !isToggledOn
        });
        this._modalService.closeModal();
    }

    private closeAgreementCompletedModal(newStatus: boolean): void {
        this.agreementFormGroup.patchValue({
            agreementCompleteCheck: !newStatus
        });
        // this.submittedByAdmin$.next(!newStatus);
        this._modalService.closeModal();
    }

    public async saveAgreementCompleted(isSubmitting$: BehaviorSubject<boolean>): Promise<void> {
        this.agreementFormGroup.updateValueAndValidity();
        this.agreementFormGroup.markAllAsTouched();

        if (!this.agreementFormGroup.invalid) {
            isSubmitting$.next(true);
            const dateAgreementSignedFormatted = this.lsqDatePipe.transform(
                this.agreementFormGroup.controls.supplierAgreementSignedDate.value,
                'yyyy-MM-dd'
            );
            await this._saveAgreementCompletedService.executeOnce(
                new SaveAgreementCompletedRequest({
                    relationshipKey: this.relationship.relationshipKey,
                    submittedByAdmin: this.agreementFormGroup.controls.agreementCompleteCheck.value,
                    supplierAgreementSignedDate: dateAgreementSignedFormatted,
                    companyName: this.relationship.supplierPreferredName
                })
            );
            this._modalService.closeModal();
            this._workflowMonitorService.markWorkflowFinished();
            await this._manageSupplierService.loadRelationship(this.relationship.relationshipKey);
            isSubmitting$.next(false);
        }
    }

    public async unsaveAgreementCompleted(isSubmitting$: BehaviorSubject<boolean>): Promise<void> {
        this.agreementFormGroup.clearValidators();
        this.agreementFormGroup.updateValueAndValidity();
        isSubmitting$.next(true);

        await this._unsaveAgreementCompletedService.executeOnce(
            new UnsaveAgreementCompletedRequest({
                relationshipKey: this.relationship.relationshipKey
            })
        );

        // Set eligibility to false on the relationship when unsaving the agreement date.
        await this._adminSuppliersService.setSuppliersEligibility(
            {
                supplierKey: this.relationship.supplierKey,
                relationshipKey: this.relationship.relationshipKey
            },
            false
        );

        this._modalService.closeModal();
        this._workflowMonitorService.markWorkflowFinished();
        await this._manageSupplierService.loadRelationship(this.relationship.relationshipKey);
        isSubmitting$.next(false);
    }

    public async setIsUsingAlternateSupplierAgreement(
        isSubmitting$: BehaviorSubject<boolean>
    ): Promise<void> {
        isSubmitting$.next(true);

        const usingAlternateSupplierAgreement = this.agreementFormGroup.controls
            .usingAlternateSupplierAgreement.value;
        await this._setIsUsingAlternateSupplierAgreementService.executeOnce(
            new SetIsUsingAlternateSupplierAgreementRequest({
                relationshipKey: this.relationship.relationshipKey,
                usingAlternateSupplierAgreement
            })
        );

        // Set eligibility to false on the relationship when turning on the alternative agreement.
        if (usingAlternateSupplierAgreement) {
            await this._adminSuppliersService.setSuppliersEligibility(
                {
                    supplierKey: this.relationship.supplierKey,
                    relationshipKey: this.relationship.relationshipKey
                },
                false
            );
        } else {
            if (this.agreementFormGroup.controls.agreementCompleteCheck.value) {
                await this._unsaveAgreementCompletedService.executeOnce(
                    new UnsaveAgreementCompletedRequest({
                        relationshipKey: this.relationship.relationshipKey
                    })
                );
            }
        }

        this._modalService.closeModal();
        this._workflowMonitorService.markWorkflowFinished();
        await this._manageSupplierService.loadRelationship(this.relationship.relationshipKey);
        isSubmitting$.next(false);
    }

    private _determineIfEnrolled(relationship: GetSupplierRelationshipDetailsResponse): boolean {
        if (!relationship.supplierEnrollments || relationship.supplierEnrollments.length === 0) {
            return false;
        }

        const latestEnrollment = relationship.supplierEnrollments
            ?.filter((enrollment) => enrollment.enrollmentStatus !== EnrollmentStatus.TRANSFERRED)
            .sort((a, b) =>
                a.createdAt < b.createdAt ? 1 : a.createdAt === b.createdAt ? 0 : -1
            )[0];

        return latestEnrollment.enrollmentStatus === EnrollmentStatus.ENROLLED;
    }

    public readonly supplierAgreementHeaderI18n = this._i18n({
        value: 'Supplier Agreement',
        description: 'Card header'
    });

    public readonly usingAlternateAgreementLabelI18n = this._i18n({
        value: 'Alternative Supplier Agreement',
        description: 'Toggle to skip the vendor agreement'
    });

    public readonly usingAlternateAgreementSubLabelI18n = this._i18n({
        value:
            'Disable the click through agreement the supplier would typically see and agree to in their enrollment flow. Another electronic or physical supplier agreement will still need to be signed and its date entered below to enable early payments.',
        description: 'Description for the alternative field'
    });

    public readonly modalAgreementAlternativeDisableHeaderI18n = this._i18n({
        value: 'Disable Alternative Supplier Agreement',
        description: 'Confirm disable modal header for agreement alternative'
    });

    public readonly modalAgreementAlternativeEnableHeaderI18n = this._i18n({
        value: 'Enable Alternative Supplier Agreement',
        description: 'Confirm enable modal header for agreement alternative'
    });

    public readonly modalAgreementCompletedTitle = this._i18n({
        value: 'Confirm Changes'
    });

    public readonly modalAgreementAlternativeConfirm18n = this._i18n({
        value: 'Save & Confirm',
        description: 'Confirm button header for agreement alternative'
    });

    public readonly modalAgreementAlternativeCancelI18n = this._i18n({
        value: 'Cancel',
        description: 'Cancel button header for agreement alternative'
    });

    public readonly selectADateI18n = this._i18n({
        value: 'Select a date:'
    });
}
